import { useEffect, useState } from 'react';
import { API } from '@unboared/react';

import { getRawSocket } from '~/utils/getRawSocket';

export type ConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

export const useConnectionStatus = () => {
    const [status, setStatus] = useState<ConnectionStatus>('connected');

    useEffect(() => {
        const unsubDisconnect = API.onDisconnect((deviceID: string) => {
            if (deviceID === API.getDeviceID()) {
                setStatus('disconnected');
            }
        });
        return unsubDisconnect;
    }, []);

    useEffect(() => {
        const unsubConnect = API.onConnect((deviceID: string) => {
            if (deviceID === API.getDeviceID()) {
                setStatus('connected');
            }
        });
        return unsubConnect;
    }, []);

    useEffect(() => {
        const unsubTempDisconnect = (API as any).onTemporaryDisconnect?.(() => {
            setStatus('reconnecting');
        });
        return unsubTempDisconnect;
    }, []);

    // Clear the "reconnecting" banner once we are actually back online.
    // Transient reconnections dispatch onDeviceBack to PEERS, not to our own
    // device, and the lib emits no self-level "reconnected" event — it only
    // clears its internal flags on `ready`. So onConnect never fires for us on
    // a wifi blip and the banner would stay forever. Listen to the raw socket
    // (re)connection instead (same access pattern as useSentrySocketBreadcrumbs).
    useEffect(() => {
        const wss = getRawSocket();
        if (!wss) return undefined;
        const onBackOnline = () => setStatus('connected');
        wss.on('connect', onBackOnline);
        const manager = wss.io;
        manager?.on('reconnect', onBackOnline);
        return () => {
            wss.off('connect', onBackOnline);
            manager?.off('reconnect', onBackOnline);
        };
    }, []);

    // Backward compatibility: isConnected is true when connected or reconnecting
    // (during reconnecting, the player is still "in the game")
    const isConnected = status !== 'disconnected';

    return { status, isConnected };
};
